// ---------------------------------------------------------------------------
// DeviceToolbar — Breakpoint selector (mobile / tablet / desktop).
// ---------------------------------------------------------------------------

import { useEditorStore } from "@/features/page-editor/store/editor-store";
import { useStudioStore } from "@/store/studio-store";
import { FileText, Monitor, Smartphone, Tablet } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { BREAKPOINT_VIEWPORTS } from "@/features/page-editor/lib/responsive";
import type { Breakpoint } from "@/features/page-editor/store/types";
import type { LucideIcon } from "lucide-react";
import { useEffect } from "react";
import { useStudioLocale } from "@/i18n";
import { viewportIsPinnedToSource } from "@/features/design-system/viewport/viewport-policy";
import { VIEWPORT_POLICY_COPY } from "@/features/design-system/viewport/viewport-policy-copy";
import {
  generatedAppViewportBreakpoint,
  resolveGeneratedAppViewportMetadata,
} from "@/features/preview/viewport/generated-app-viewport";

interface DeviceOption {
  bp: Breakpoint;
  icon: LucideIcon;
}

interface DeviceToolbarProps {
  activeView?: "app" | "report";
  onSelectApp?: () => void;
  onSelectReport?: () => void;
  showReport?: boolean;
  pageId?: string | null;
}

/**
 * The presets, WITHOUT their words and without their numbers.
 *
 * Both used to be typed here: `label: "Mobile (375x812)"` restated the size
 * that `BREAKPOINT_VIEWPORTS.mobile` already owns, so a preset resized
 * upstream would have kept announcing the old frame. The label is now
 * composed at render time from the locale's word plus that table, which is
 * also where the device-dimension judgement lands: `Mobile` is a word and is
 * translated, `375x812` is a measurement and is not.
 */
const devices: DeviceOption[] = [
  { bp: "mobile", icon: Smartphone },
  { bp: "tablet", icon: Tablet },
  { bp: "desktop", icon: Monitor },
];

export function DeviceToolbar({
  activeView = "app",
  onSelectApp,
  onSelectReport,
  showReport = false,
  pageId,
}: DeviceToolbarProps = {}) {
  const activeBreakpoint = useEditorStore((s) => s.activeBreakpoint);
  const editorActivePageId = useEditorStore((s) => s.activePageId);
  const setBreakpoint = useEditorStore((s) => s.setBreakpoint);
  // The user's OWN pick is recorded here rather than inside `setBreakpoint`,
  // because that action is also driven programmatically — every project open
  // sets it, and a project pinned to its source viewport is held at mobile —
  // and none of those are a preference to remember. (The visual review used to
  // walk the frame here too; it captures offscreen now and touches nothing.)
  const currentProjectId = useStudioStore((s) => s.currentProjectId);
  const setPreviewDeviceChoice = useStudioStore((s) => s.setPreviewDeviceChoice);
  // A project pinned to its source viewport sends the breakpoint straight back
  // to mobile — the preview's effect depends on `activeBreakpoint`, so the
  // revert is immediate and unconditional. Offering the other two as ordinary
  // buttons was a lie: the creator pressed Desktop, watched it flick back, and
  // was told nothing.
  const currentProject = useStudioStore((s) => s.currentProject);
  const studioCurrentPageId = useStudioStore((s) => s.currentPageId);
  // The shared toolbar lives in two planes. The editor owns its active page;
  // preview callers pass their Studio page explicitly. Falling back to the
  // Studio page keeps the toolbar truthful while the editor is still opening.
  const viewportPageId =
    pageId === undefined
      ? editorActivePageId ?? studioCurrentPageId
      : pageId;
  const declareViewportPolicy = useStudioStore((s) => s.declareViewportPolicy);
  const generatedViewport = resolveGeneratedAppViewportMetadata(currentProject, viewportPageId);
  const generatedBreakpoint = generatedAppViewportBreakpoint(generatedViewport);
  const pinned = viewportIsPinnedToSource(
    currentProject,
    generatedViewport != null,
  );
  useEffect(() => {
    if (pinned && generatedBreakpoint && activeBreakpoint !== generatedBreakpoint) {
      setBreakpoint(generatedBreakpoint);
    }
  }, [activeBreakpoint, generatedBreakpoint, pinned, setBreakpoint]);
  const { strings, locale } = useStudioLocale();
  const deviceCopy = strings.ide.device;
  const policyCopy = VIEWPORT_POLICY_COPY[locale];
  const deviceLabel = (bp: Breakpoint) => {
    const viewport = BREAKPOINT_VIEWPORTS[bp];
    return deviceCopy.framed(deviceCopy[bp], viewport.width, viewport.height);
  };

  return (
    <div className="flex items-center gap-1">
      {devices.map((d) => (
        <Tooltip key={d.bp}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => {
                setBreakpoint(d.bp);
                setPreviewDeviceChoice(currentProjectId, d.bp);
                onSelectApp?.();
              }}
              data-testid={`breakpoint-${d.bp}`}
              disabled={pinned && d.bp !== generatedBreakpoint}
              // The reason travels on the accessible name, not only in a
              // hover tooltip: a disabled control a keyboard user lands on
              // should say why it is off without a pointer.
              aria-label={
                pinned && d.bp !== generatedBreakpoint
                  ? `${deviceLabel(d.bp)} — ${policyCopy.pinnedToSource}`
                  : deviceLabel(d.bp)
              }
              aria-pressed={activeView === "app" && activeBreakpoint === d.bp}
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-md border border-transparent transition-colors",
                activeView === "app" && activeBreakpoint === d.bp
                  ? "border-border/70 bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <d.icon size={16} />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {pinned && d.bp !== generatedBreakpoint
                ? policyCopy.pinnedToSource
                : deviceLabel(d.bp)}
            </p>
          </TooltipContent>
        </Tooltip>
      ))}
      {pinned && (
        // The pin is the PROJECT's decision, so the way out is offered at
        // project level rather than as a per-tab override the next open would
        // forget. Shown only while pinned: a control that is always there is
        // chrome, and chrome is what people stop reading.
        <button
          type="button"
          onClick={() => {
            void declareViewportPolicy("responsive");
          }}
          data-testid="viewport-policy-responsive"
          className="ml-1 rounded-md border border-border/70 px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          {policyCopy.makeResponsive}
        </button>
      )}
      {showReport && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onSelectReport}
              data-testid="breakpoint-report"
              aria-pressed={activeView === "report"}
              aria-label={deviceCopy.document}
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-md border border-transparent transition-colors",
                activeView === "report"
                  ? "border-border/70 bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <FileText size={16} />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{`${deviceCopy.document} · ${deviceCopy.a4Portrait}`}</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
